import React, { forwardRef, useEffect, useState } from "react";
import { CardBody, CardFooter, CardHeader, Col, Row } from "reactstrap";
import { formatDateLongFormat } from "utils/date";
import "utils/jsdocTypes";
import InvoiceLineItem from "./invoiceLineItem.component";
import InvoiceSummaryLine from "./invoiceSummaryLine.component";
import "./invoice.style.css";
import InvoiceHeader from "./invoiceHeader.component";
import InvoiceBillTo from "./invoiceBillTo.component";
import InvoiceTableHeading from "./invoiceTableHeading.component";

/**
 * @typedef {Object} Props
 * @property {AdminTransactionDetails} transaction
 * @property {AdminCustomerDetails} customer
 * @property {React.Dispatch<React.SetStateAction<boolean>>} onCardLoad
 */

/**
 * @function InvoiceCard
 * @param {Props} props
 * @type {React.ForwardRefExoticComponent<React.PropsWithoutRef<Props> & React.RefAttributes<HTMLDivElement>>}
 */
const InvoiceCard = forwardRef(({ transaction, customer, onCardLoad }, ref) => {
  const [transactionSum, setTransactionSum] = useState(0);
  const [transactionSubtotal, setTransactionSubtotal] = useState(0);

  /**
   * @type {InvoiceRowData[]} rowData
   */
  const [rowData, setRowData] = useState([]);

  useEffect(() => {
    setRowData([
      {
        quantity: transaction.price,
        description: "Highly Encrypted Cloud Storage Volume (GB)",
        unitPrice: 1,
        total: transaction.price,
      },
    ]);
  }, []);

  useEffect(() => {
    let sum = 0;

    rowData.map((row) => {
      sum += row.total;
    });

    setTransactionSum(sum);
    setTransactionSubtotal(sum);
  }, [rowData]);

  useEffect(() => {
    if (onCardLoad) {
      const timer = setTimeout(() => {
        if (onCardLoad) {
          onCardLoad();
        }
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [onCardLoad]);

  return (
    <div ref={ref} className="invoiceWrapper">
      <CardHeader className="card-light">
        <InvoiceHeader
          companyName="WowCloudNetwork"
          address1="1818 M.L.K Jr Blvd Box 336"
          city="Chapel Hill"
          state="NC"
          zip="27514"
          imgUrl="/logo.png"
        />
      </CardHeader>
      <CardBody className="card-light">
        <Row className="invoiceHeadingBar">
          <Col sm="6">Invoice {transaction.invoicenumber}</Col>
          <Col sm="6" className="d-flex justify-content-end">
            {formatDateLongFormat(transaction.datetimecreated).toUpperCase()}
          </Col>
        </Row>

        <InvoiceBillTo customer={customer} />
        <InvoiceTableHeading
          headingData={[
            { colSize: 2, headingValue: "quantity" },
            { colSize: 6, headingValue: "description" },
            { colSize: 2, headingValue: "unit price" },
            { colSize: 2, headingValue: "total" },
          ]}
        />

        {Array.from({ length: Math.max(10, rowData.length) }, (_, index) => {
          const row = rowData[index] || {}; // Use empty object if no data
          return <InvoiceLineItem key={index} invoiceRowData={row} />;
        })}

        <InvoiceSummaryLine
          summaryLineData={{
            lineField: "subtotal",
            lineValue: "$" + transactionSubtotal,
            isBold: false,
          }}
        />
        <InvoiceSummaryLine
          summaryLineData={{
            lineField: "sales",
            lineValue: "$0",
            isBold: false,
          }}
        />
        <InvoiceSummaryLine
          summaryLineData={{
            lineField: "shipping & Handling",
            lineValue: "Digital Delivery",
            isBold: false,
          }}
        />
        <InvoiceSummaryLine
          summaryLineData={{
            lineField: "total due by date",
            lineValue: "$" + transactionSum,
            isBold: true,
          }}
        />
      </CardBody>

      <CardFooter className="card-light invoiceFooter">
        <Row>
          <Col className="col-sm-auto offset-sm-5">
            Thank you for your business!
          </Col>
        </Row>
      </CardFooter>
    </div>
  );
});

InvoiceCard.displayName = "InvoiceCard";

export default InvoiceCard;
